import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { themeClass as pickThemeClass } from '../theme/themeClasses';
import { THEME_STORAGE_KEY } from '../theme/themeTokens';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};

function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* ignore */
  }
  return 'dark';
}

function applyDocumentTheme(theme) {
  const isDark = theme === 'dark';
  const root = document.documentElement;
  const body = document.body;

  root.setAttribute('data-theme', theme);
  root.classList.toggle('dark', isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';
  body.classList.toggle('app-dark-theme', isDark);
  body.classList.toggle('app-light-theme', !isDark);
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => readStoredTheme());

  useEffect(() => {
    applyDocumentTheme(theme);
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'light' ? 'light' : 'dark');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  const isDark = theme === 'dark';

  const themeClassFn = useCallback(
    (map, extra = '') => pickThemeClass(isDark, map, extra),
    [isDark]
  );

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isDark,
      themeClass: themeClassFn,
    }),
    [theme, setTheme, toggleTheme, isDark, themeClassFn]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
